import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import type { DocumentShareData, CollaboratorPermission } from '@corner/shared';

const F = '"Geist", system-ui, sans-serif';

interface ShareDocumentModalProps {
  conversationId: string;
  onClose: () => void;
  getToken?: () => string | null;
  /** Current user info for the "you" row */
  currentUser?: { name: string; email: string; avatarUrl?: string | null };
}

export default function ShareDocumentModal({
  conversationId,
  onClose,
  getToken,
  currentUser,
}: ShareDocumentModalProps) {
  const [shareData, setShareData] = useState<DocumentShareData | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [permDropdown, setPermDropdown] = useState<string | null>(null);
  const [reshareConfirm, setReshareConfirm] = useState<string | null>(null);

  const headers = useCallback(() => {
    const token = getToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  // Fetch share settings on mount
  useEffect(() => {
    setLoading(true);
    axios.get<DocumentShareData>(`/api/sharing/${conversationId}`, { headers: headers() })
      .then((res) => setShareData(res.data))
      .catch(() => { /* silent — will show empty state */ })
      .finally(() => setLoading(false));
  }, [conversationId, headers]);

  // Invite collaborator
  const sendInvite = useCallback(async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setReshareConfirm(null);
    setInviting(true);
    try {
      const res = await axios.post<DocumentShareData>(
        `/api/sharing/${conversationId}/invite`,
        { email, displayName: email.split('@')[0], permission: 'edit' as CollaboratorPermission },
        { headers: headers() },
      );
      setShareData(res.data);
      setInviteEmail('');
      toast({ title: 'Invited', description: `${email} can now collaborate on this document.` });
    } catch {
      toast({ title: 'Failed to invite', variant: 'destructive' });
    } finally {
      setInviting(false);
    }
  }, [inviteEmail, conversationId, headers]);

  const handleInvite = useCallback(() => {
    const email = inviteEmail.trim();
    if (!email) return;
    const existing = shareData?.collaborators.find((c) => c.email.toLowerCase() === email.toLowerCase());
    if (existing) {
      setReshareConfirm(email);
      return;
    }
    sendInvite();
  }, [inviteEmail, shareData, sendInvite]);

  // Update permission
  const handlePermissionChange = useCallback(async (email: string, permission: CollaboratorPermission) => {
    try {
      const res = await axios.patch<DocumentShareData>(
        `/api/sharing/${conversationId}/collaborators/${encodeURIComponent(email)}`,
        { permission },
        { headers: headers() },
      );
      setShareData(res.data);
      setPermDropdown(null);
    } catch {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  }, [conversationId, headers]);

  // Remove collaborator
  const handleRemove = useCallback(async (email: string) => {
    try {
      const res = await axios.delete<DocumentShareData>(
        `/api/sharing/${conversationId}/collaborators/${encodeURIComponent(email)}`,
        { headers: headers() },
      );
      setShareData(res.data);
      toast({ title: 'Removed' });
    } catch {
      toast({ title: 'Failed to remove', variant: 'destructive' });
    }
  }, [conversationId, headers]);

  // Copy link — enable link sharing if not already, then copy the token URL
  const handleCopyLink = useCallback(async () => {
    try {
      // Enable link sharing and get token
      const res = await axios.post<DocumentShareData & { linkToken?: string }>(
        `/api/sharing/${conversationId}/link`,
        { enabled: true },
        { headers: headers() },
      );
      const token = res.data.linkToken;
      const url = token
        ? `${window.location.origin}/app/shared/${token}`
        : `${window.location.origin}/app?shared=${conversationId}`;
      await navigator.clipboard.writeText(url);
      setShareData(res.data);
      toast({ title: 'Link copied' });
    } catch {
      toast({ title: 'Failed to copy link', variant: 'destructive' });
    }
  }, [conversationId, headers]);

  const collaborators = shareData?.collaborators ?? [];

  return (
    <div style={MODAL_SHELL} onClick={() => setPermDropdown(null)}>
      {/* Header */}
      <div style={HEADER}>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="7" cy="7" r="3" stroke="#2E2318" strokeWidth="1.3" />
          <path d="M1.5 16C1.5 12.5 4 10.5 7 10.5C10 10.5 12.5 12.5 12.5 16" stroke="#2E2318" strokeWidth="1.3" strokeLinecap="round" />
          <path d="M12 6.5h4M14 4.5v4" stroke="#2E2318" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#2E2318', flexGrow: 1, lineHeight: '18px', fontFamily: F }}>Share document</span>
        <button type="button" onClick={onClose} style={CLOSE_BTN}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="#9A8E82" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>
      <div style={{ width: '100%', height: 1, backgroundColor: '#EDE8E2', flexShrink: 0 }} />

      {/* Invite section */}
      <div style={{ display: 'flex', flexDirection: 'column', padding: '16px 24px 0', gap: 8 }}>
        <span style={LABEL}>Invite people</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleInvite(); }}
            placeholder="Email address or name..."
            disabled={inviting}
            style={INPUT}
          />
          <button
            type="button"
            onClick={handleInvite}
            disabled={inviting || !inviteEmail.trim()}
            style={{ ...INVITE_BTN, opacity: inviting || !inviteEmail.trim() ? 0.5 : 1 }}
          >
            {inviting ? '...' : 'Invite'}
          </button>
        </div>
      </div>

      {/* Reshare confirmation */}
      {reshareConfirm && (
        <div style={{ margin: '0 24px', padding: '12px 14px', borderRadius: 8, backgroundColor: '#FFF8F3', border: '1px solid #F0DFD0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#6B5E52', fontFamily: F, lineHeight: '18px' }}>
            <strong>{reshareConfirm}</strong> already has access. Resend the invitation?
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={sendInvite} style={{ ...INVITE_BTN, padding: '6px 14px', fontSize: 12 }}>
              Resend
            </button>
            <button type="button" onClick={() => setReshareConfirm(null)} style={{ background: 'none', border: '1px solid #EDE8E2', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 500, color: '#6B5E52', cursor: 'pointer', fontFamily: F }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* People with access */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '20px 24px 10px', gap: 8 }}>
        <span style={LABEL}>People with access</span>
        <div style={{ flexGrow: 1, height: 1, backgroundColor: '#EDE8E2' }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', padding: '0 24px', gap: 4 }}>
        {/* Owner row (current user) */}
        {currentUser && (
          <div style={PERSON_ROW}>
            <div style={{ ...AVATAR, backgroundColor: '#8B7355' }}>
              {currentUser.avatarUrl ? (
                <img src={currentUser.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: 12, fontWeight: 600, color: '#FFFFFF' }}>{initials(currentUser.name)}</span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#2E2318', fontFamily: F }}>{currentUser.name}</span>
                <span style={{ fontSize: 11, fontWeight: 400, color: '#B8ACA2', fontFamily: F }}>(you)</span>
              </div>
              <span style={{ fontSize: 12, fontWeight: 400, color: '#9A8E82', fontFamily: F }}>{currentUser.email}</span>
            </div>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#9A8E82', fontFamily: F }}>Owner</span>
          </div>
        )}

        {/* Collaborator rows */}
        {loading && collaborators.length === 0 && (
          <div style={{ padding: '12px 0', fontSize: 12, color: '#9A8E82', fontFamily: F }}>Loading...</div>
        )}
        {collaborators.map((collab) => (
          <div key={collab.email} style={PERSON_ROW}>
            <div style={{ ...AVATAR, backgroundColor: collabColor(collab.email), position: 'relative' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#FFFFFF' }}>{initials(collab.displayName)}</span>
              {/* Pending indicator (no green dot until accepted) */}
              {collab.acceptedAt && (
                <div style={{ position: 'absolute', bottom: -1, right: -1, width: 10, height: 10, borderRadius: '50%', backgroundColor: '#1B7A4B', border: '2px solid #FDFCFB' }} />
              )}
              {!collab.acceptedAt && (
                <div style={{ position: 'absolute', bottom: -1, right: -1, width: 10, height: 10, borderRadius: '50%', backgroundColor: '#D4C9BE', border: '2px solid #FDFCFB' }} />
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#2E2318', fontFamily: F }}>{collab.displayName}</span>
              <span style={{ fontSize: 12, fontWeight: 400, color: '#9A8E82', fontFamily: F }}>{collab.email}</span>
            </div>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPermDropdown(permDropdown === collab.email ? null : collab.email); }}
                style={PERM_BTN}
              >
                <span style={{ fontSize: 12, fontWeight: 400, color: '#6B5E52', fontFamily: F }}>
                  {collab.permission === 'edit' ? 'Can edit' : collab.permission === 'comment' ? 'Can comment' : 'Can view'}
                </span>
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M2 3L4 5L6 3" stroke="#9A8E82" strokeWidth="1" strokeLinecap="round" /></svg>
              </button>
              {permDropdown === collab.email && (
                <div style={PERM_DROPDOWN} onClick={(e) => e.stopPropagation()}>
                  {(['view', 'comment', 'edit'] as CollaboratorPermission[]).map((perm) => (
                    <button
                      key={perm}
                      type="button"
                      onClick={() => handlePermissionChange(collab.email, perm)}
                      style={{ ...PERM_DROPDOWN_ITEM, fontWeight: collab.permission === perm ? 500 : 400 }}
                    >
                      {perm === 'edit' ? 'Can edit' : perm === 'comment' ? 'Can comment' : 'Can view'}
                      {collab.permission === perm && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.5l2.5 2.5 4.5-5" stroke="#D97645" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      )}
                    </button>
                  ))}
                  <div style={{ height: 1, backgroundColor: '#EDE8E2', margin: '4px 0' }} />
                  <button
                    type="button"
                    onClick={() => { handleRemove(collab.email); setPermDropdown(null); }}
                    style={{ ...PERM_DROPDOWN_ITEM, color: '#E8534A' }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={FOOTER}>
        <button type="button" onClick={handleCopyLink} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: F }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
            <path d="M5.5 8.5L8.5 5.5M4.5 9.5l-1 1a2 2 0 002.83 0l1-1M9.5 4.5l1-1a2 2 0 00-2.83 0l-1 1" stroke="#9A8E82" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: 12, fontWeight: 500, color: '#5E6AD2', flexGrow: 1 }}>Copy link</span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle cx="6" cy="6" r="4.5" stroke="#9A8E82" strokeWidth="1" />
            <path d="M1.5 6h9M6 1.5c1.5 1.5 2 3 2 4.5s-.5 3-2 4.5M6 1.5c-1.5 1.5-2 3-2 4.5s.5 3 2 4.5" stroke="#9A8E82" strokeWidth="1" />
          </svg>
          <span style={{ fontSize: 12, fontWeight: 400, color: '#9A8E82', fontFamily: F }}>Anyone with link can view</span>
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M2 3L4 5L6 3" stroke="#9A8E82" strokeWidth="1" strokeLinecap="round" /></svg>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name[0]?.toUpperCase() ?? '?';
}

const COLLAB_COLORS = ['#E05A33', '#1B7A4B', '#5E6AD2', '#9C5BBD', '#D97645', '#4285F4'];
function collabColor(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = ((hash << 5) - hash + email.charCodeAt(i)) | 0;
  return COLLAB_COLORS[Math.abs(hash) % COLLAB_COLORS.length];
}

// ─── Styles ─────────────────────────────────────

const MODAL_SHELL: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: 440,
  maxHeight: '80vh',
  borderRadius: 14,
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: '#FDFCFB',
  boxShadow: 'rgba(0,0,0,0.25) 0px 16px 60px, rgba(0,0,0,0.05) 0px 0px 0px 1px',
  fontFamily: F,
};

const HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '20px 24px 16px',
  gap: 10,
};

const CLOSE_BTN: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
};

const LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: '#9A8E82',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  lineHeight: '14px',
  fontFamily: F,
};

const INPUT: React.CSSProperties = {
  flexGrow: 1,
  padding: '9px 12px',
  borderRadius: 8,
  border: '1px solid #EDE8E2',
  backgroundColor: '#FFFFFF',
  fontSize: 13,
  fontWeight: 400,
  color: '#2E2318',
  fontFamily: F,
  lineHeight: '16px',
  outline: 'none',
  boxSizing: 'border-box',
};

const INVITE_BTN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '9px 16px',
  borderRadius: 8,
  border: 'none',
  backgroundColor: '#2E2318',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  color: '#FDFCFB',
  fontFamily: F,
  lineHeight: '16px',
  flexShrink: 0,
};

const PERSON_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px 0',
  gap: 10,
};

const AVATAR: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const PERM_BTN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid #EDE8E2',
  background: 'none',
  cursor: 'pointer',
  fontFamily: F,
};

const PERM_DROPDOWN: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  right: 0,
  marginTop: 4,
  minWidth: 140,
  backgroundColor: '#FDFCFB',
  border: '1px solid #E4DDD4',
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  zIndex: 10,
  padding: 4,
};

const PERM_DROPDOWN_ITEM: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  borderRadius: 6,
  fontFamily: F,
  fontSize: 12,
  color: '#2E2318',
  textAlign: 'left',
};

const FOOTER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '16px 24px 20px',
  gap: 10,
  borderTop: '1px solid #EDE8E2',
  marginTop: 12,
};
